import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, MapPin, ChevronDown, ArrowLeft } from 'lucide-react';
import { companyInfo, getBranch } from '../data/companyInfo';
import { trackPhoneClick } from '../lib/analytics';

// ---- Navigation models ----
//
// When the URL is under /[branch]-removals/*, the Header SWITCHES into
// "branch mode": the entire nav (desktop + mobile) + contact strip + quote
// button become scoped to that branch. When on any other page, we render the
// default main-site nav.
//
// This gives each branch the feeling of a mini-site inside HomeGo without a
// second nav bar.

const MAIN_NAV = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/house-removals', label: 'House Removals' },
  { to: '/office-relocation', label: 'Office Relocation' },
  { to: '/move-cost-estimator', label: 'Cost Estimator' },
  {
    label: 'Moves',
    children: [
      { to: '/long-distance-removals',  label: 'Long-Distance Moves',  hint: 'UK & Europe · fixed price · one crew end-to-end' },
      { to: '/short-distance-removals', label: 'Short-Distance Moves', hint: 'Local moves · same-day available · tailored quote' },
      { to: '/commercial-removals',     label: 'Commercial Removals',  hint: 'Shops, warehouses & business moves · out-of-hours crews' },
      { to: '/removals-and-storage',    label: 'Removals & Storage',   hint: 'House moves with storage arranged alongside' },
      { to: '/get-a-quote',             label: 'Get a Free Quote',     hint: '60-second form · callback in 2 mins' },
    ],
  },
  {
    label: 'Our Branches',
    children: [
      { to: '/burnham-removals',   label: 'Burnham HQ',  hint: 'Head Office · Berkshire & Thames Valley · SL1 7DD' },
      { to: '/birmingham-removals', label: 'Birmingham', hint: 'West Midlands · Walford Road, Sparkbrook B11 1NP' },
      { to: '/uxbridge-removals',   label: 'Uxbridge',   hint: 'West London / Middlesex · 195 Pield Heath Road UB8 3NR' },
      { to: '/oxfordshire-removals', label: 'Oxfordshire', hint: 'Oxford / Thames Valley North · 6 Mandelbrook Drive, Littlemore OX4 4TN' },
      { to: '/removals-maidenhead', label: 'Maidenhead', hint: 'Royal Borough · 39 Queen Street, Maidenhead SL6 1NB' },
      { to: '/removals-reading', label: 'Reading', hint: 'Thames Valley · 15 Queens Walk, Reading RG1 7QF' },
      { to: '/removals-milton-keynes', label: 'Milton Keynes', hint: 'Buckinghamshire · Marlborough Gate MK9 3XA' },
      { to: '/london-removals', label: 'London', hint: 'Central London · Great Russell Street, Bloomsbury WC1B 3PA' },
      { to: '/find-a-branch',       label: 'Find a Branch', hint: 'Interactive map · directions · all 8 branches' },
    ],
  },
  { to: '/areas-we-cover', label: 'Areas' },
  { to: '/faqs',    label: 'FAQs' },
  { to: '/contact', label: 'Contact' },
  { to: '/about',   label: 'About' },
];

const BRANCH_NAV_FACTORY = (hub) => [
  { to: hub,               label: 'Home',     exact: true },
  { to: `${hub}/areas`,    label: 'Areas' },
  { to: `${hub}/services`, label: 'Services' },
  { to: '/move-cost-estimator', label: 'Cost Estimator' },
  {
    label: 'Moves',
    children: [
      { to: '/long-distance-removals',  label: 'Long-Distance Moves',  hint: 'UK & Europe · fixed price · one crew end-to-end' },
      { to: '/short-distance-removals', label: 'Short-Distance Moves', hint: 'Local moves · same-day available · tailored quote' },
      { to: '/commercial-removals',     label: 'Commercial Removals',  hint: 'Shops, warehouses & business moves · out-of-hours crews' },
      { to: '/removals-and-storage',    label: 'Removals & Storage',   hint: 'House moves with storage arranged alongside' },
      { to: '/get-a-quote',             label: 'Get a Free Quote',     hint: '60-second form · callback in 2 mins' },
    ],
  },
  { to: `${hub}/contact`,  label: 'Contact' },
  { to: `${hub}/faqs`,     label: 'FAQs' },
  { to: `${hub}/about`,    label: 'About' },
  { to: `${hub}/blog`,     label: 'Blog' },
];

// Map URL prefix → branch key. Ordered by specificity (longest first not needed
// because all 3 prefixes are the same length pattern).
const BRANCH_PREFIXES = [
  { prefix: '/burnham-removals',    key: 'burnham',    accent: 'gold' },
  { prefix: '/birmingham-removals', key: 'birmingham', accent: 'blue' },
  { prefix: '/uxbridge-removals',   key: 'uxbridge',   accent: 'purple' },
  { prefix: '/oxfordshire-removals', key: 'oxfordshire', accent: 'gold' },
  { prefix: '/removals-maidenhead', key: 'maidenhead', accent: 'gold' },
  { prefix: '/removals-reading',    key: 'reading',    accent: 'gold' },
];

const ACCENT = {
  gold:   { text: 'text-gold-400',   bar: 'bg-gold-500/10',   ring: 'border-gold-500/30' },
  blue:   { text: 'text-blue-300',   bar: 'bg-blue-500/10',   ring: 'border-blue-500/30' },
  purple: { text: 'text-purple-300', bar: 'bg-purple-500/10', ring: 'border-purple-500/30' },
};

const testId = (label) =>
  `nav-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;

function detectBranch(pathname) {
  return BRANCH_PREFIXES.find((b) => pathname === b.prefix || pathname.startsWith(`${b.prefix}/`));
}

// ---------- Desktop dropdown (used for "Our Branches" in main mode) ---------- //
function DesktopDropdown({ item, pathname }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const anyChildActive = item.children.some((c) => pathname.startsWith(c.to));
  return (
    <div ref={containerRef} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu"
        className={`flex items-center gap-1 text-sm font-semibold transition-colors ${anyChildActive ? 'text-gold-400' : 'text-gray-200 hover:text-gold-400'}`}
        data-testid={`${testId(item.label)}-toggle`}>
        {item.label}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-3 min-w-[280px] bg-black border border-gold-500/30 rounded-lg shadow-xl shadow-black/60 overflow-hidden z-50" data-testid={`${testId(item.label)}-panel`}>
          <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-gold-400/80 border-b border-gold-500/10 bg-gold-500/5">Choose your branch</div>
          {item.children.map((c) => (
            <Link key={c.label} to={c.to} onClick={() => setOpen(false)} role="menuitem"
              className={`block px-4 py-3 border-b border-gold-500/10 last:border-b-0 transition-colors ${pathname.startsWith(c.to) ? 'bg-gold-500/10' : 'hover:bg-gold-500/5'}`}
              data-testid={testId(c.label)}>
              <div className={`text-sm font-semibold ${pathname.startsWith(c.to) ? 'text-gold-400' : 'text-white'}`}>{c.label}</div>
              {c.hint && <div className="text-xs text-gray-400 mt-0.5">{c.hint}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Mobile expandable group (main-mode "Our Branches") ---------- //
function MobileGroup({ item, pathname, onNavigate }) {
  const [expanded, setExpanded] = useState(item.children.some((c) => pathname.startsWith(c.to)));
  return (
    <div className="border-b border-gold-500/10" data-testid={`mobile-${testId(item.label)}-group`}>
      <button type="button" onClick={() => setExpanded((x) => !x)} aria-expanded={expanded}
        className="w-full flex items-center justify-between py-2 text-sm font-semibold text-gray-200 hover:text-gold-400"
        data-testid={`mobile-${testId(item.label)}-toggle`}>
        {item.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="pb-2 pl-3 flex flex-col gap-1" data-testid={`mobile-${testId(item.label)}-panel`}>
          {item.children.map((c) => (
            <Link key={c.label} to={c.to} onClick={onNavigate}
              className={`py-2 text-sm ${pathname.startsWith(c.to) ? 'text-gold-400 font-semibold' : 'text-gray-300 hover:text-gold-400'}`}
              data-testid={`mobile-${testId(c.label)}`}>
              {c.label}
              {c.hint && <span className="block text-[11px] text-gray-500 mt-0.5">{c.hint}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const closeMobile = () => setOpen(false);

  // --- Branch detection ---
  const branchMatch = detectBranch(pathname);
  const branchMode = Boolean(branchMatch);
  const branch = branchMode ? getBranch(branchMatch.key) : null;
  const accent = branchMode ? ACCENT[branchMatch.accent] : ACCENT.gold;
  const hub = branchMatch?.prefix;

  // --- Active nav set ---
  const navLinks = branchMode ? BRANCH_NAV_FACTORY(hub) : MAIN_NAV;

  // --- Contact strip fields ---
  const stripPhone = branchMode ? branch.phone : companyInfo.mobile;
  const stripPhoneHref = branchMode ? branch.phoneHref : companyInfo.mobileHref;
  const stripLandline = branchMode ? branch.landline : companyInfo.landline;
  const stripLandlineHref = branchMode ? branch.landlineHref : companyInfo.landlineHref;
  const stripEmail = branchMode ? branch.email : companyInfo.email;
  const stripEmailHref = branchMode ? branch.emailHref : companyInfo.emailHref;
  const stripAddr = branchMode ? `${branch.addressLine1}, ${branch.postcode}` : `${companyInfo.addressLine1}, ${companyInfo.postcode}`;

  const isActive = (l) => {
    if (branchMode) {
      if (l.exact) return pathname === l.to || pathname === `${l.to}/`;
      return pathname === l.to || pathname.startsWith(`${l.to}/`);
    }
    return pathname === l.to;
  };

  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-xl border-b border-gold-500/20" data-testid="site-header">
      {/* Top contact strip — swaps per branch */}
      <div className={`border-b border-gold-500/10 ${branchMode ? accent.bar : 'bg-gold-500/5'}`}>
        <div className="container mx-auto px-4 flex items-center justify-between py-1.5 text-xs md:text-sm">
          <div className="flex items-center gap-4">
            {branchMode && (
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider border ${accent.ring} ${accent.text}`} data-testid="header-branch-badge">
                {branch.shortLabel}
              </span>
            )}
            <a
              href={stripPhoneHref}
              onClick={() => trackPhoneClick({ phone: stripPhone, label: 'header-strip-mobile', branch: branchMatch?.key || 'main' })}
              className={`flex items-center gap-1.5 font-semibold hover:text-white ${branchMode ? accent.text : 'text-gold-400'}`}
              data-testid="header-mobile-link"
            >
              <Phone className="w-3.5 h-3.5" /> <span className="text-[10px] text-gray-400 font-normal uppercase tracking-wider hidden md:inline">Mob</span> {stripPhone}
            </a>
            {stripLandline && (
              <a
                href={stripLandlineHref}
                onClick={() => trackPhoneClick({ phone: stripLandline, label: 'header-strip-landline', branch: branchMatch?.key || 'main' })}
                className={`hidden md:flex items-center gap-1.5 font-semibold hover:text-white ${branchMode ? accent.text : 'text-gold-400'}`}
                data-testid="header-landline-link"
              >
                <Phone className="w-3.5 h-3.5" /> <span className="text-[10px] text-gray-400 font-normal uppercase tracking-wider">Tel</span> {stripLandline}
              </a>
            )}
            <a href={stripEmailHref} className="hidden lg:flex items-center gap-1.5 text-gray-300 hover:text-gold-400 break-all" data-testid="header-email-link">
              <Mail className="w-3.5 h-3.5" /> {stripEmail}
            </a>
            <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold bg-green-500/20 text-green-400 border border-green-500/40" data-testid="header-24-7-badge">
              OPEN 24/7
            </span>
          </div>
          <div className="hidden md:flex items-center gap-1.5 text-gray-400">
            <MapPin className="w-3.5 h-3.5 text-gold-400" /> {stripAddr}
          </div>
        </div>
      </div>

      {/* Main nav row */}
      <div className="container mx-auto px-4 flex items-center justify-between py-3">
        <Link to={branchMode ? hub : '/'} className="flex items-center gap-3" data-testid="header-logo-link">
          <picture>
            <source srcSet={companyInfo.logo} type="image/webp" />
            <img src={companyInfo.logoFallback} alt="HomeGo Removals & Packing Ltd logo" width="84" height="50" className="h-10 md:h-12 w-auto rounded-sm" loading="eager" decoding="async" fetchPriority="high" />
          </picture>
          <div className="leading-tight">
            <div className="text-white font-bold text-sm md:text-lg">{companyInfo.shortName}</div>
            <div className={`text-[11px] md:text-xs ${branchMode ? accent.text : 'text-gold-400'}`}>
              {branchMode ? branch.shortLabel : `Reg® ${companyInfo.registrationNumber}`}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5" aria-label="Primary">
          {navLinks.map((l) => (
            l.children ? (
              <DesktopDropdown key={l.label} item={l} pathname={pathname} />
            ) : (
              <Link key={l.label} to={l.to}
                className={`text-sm font-semibold transition-colors ${isActive(l) ? (branchMode ? accent.text : 'text-gold-400') : 'text-gray-200 hover:text-gold-400'}`}
                data-testid={testId(`${branchMode ? branchMatch.key + '-' : ''}${l.label}`)}>
                {l.label}
              </Link>
            )
          ))}
          {branchMode && (
            <Link to="/" className="text-[11px] text-gray-500 hover:text-gold-400 uppercase tracking-wider ml-2 flex items-center gap-1" data-testid="header-back-to-main">
              <ArrowLeft className="w-3 h-3" /> Main site
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/get-a-quote"
            className={`hidden md:inline-flex items-center gap-2 font-bold px-4 py-2.5 min-h-[44px] rounded-md text-sm transition-all shadow-lg shadow-gold-500/20 bg-gradient-to-r from-gold-500 to-gold-400 hover:from-gold-400 hover:to-gold-500 text-black`}
            aria-label="Open the free quote form page"
            data-testid="header-quote-btn">
            Get Free Quote
          </Link>
          <a
            href={stripPhoneHref}
            onClick={() => trackPhoneClick({ phone: stripPhone, label: 'header-mobile-call-btn', branch: branchMatch?.key || 'main' })}
            className="md:hidden inline-flex items-center gap-1.5 bg-gold-500 text-black font-bold px-4 py-2.5 min-h-[44px] rounded-md text-sm"
            data-testid="header-call-btn"
          >
            <Phone className="w-4 h-4" /> Call
          </a>
          <button className="lg:hidden text-gold-400 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Toggle navigation menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="header-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <nav className="lg:hidden bg-black border-t border-gold-500/20 max-h-[calc(100dvh-120px)] overflow-y-auto overscroll-contain" aria-label="Mobile primary" data-testid="mobile-nav">
          <div className="container mx-auto px-4 py-3 flex flex-col">
            {/* Top-of-drawer Get Free Quote CTA — primary mobile conversion path */}
            <Link to="/get-a-quote" onClick={closeMobile}
              className="mb-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold-500 to-gold-400 text-black font-bold py-3 px-4 rounded-md text-sm shadow-lg shadow-gold-500/20"
              aria-label="Open the free quote form page"
              data-testid="mobile-header-quote-btn">
              Get Free Quote
            </Link>
            {navLinks.map((l) => (
              l.children ? (
                <MobileGroup key={l.label} item={l} pathname={pathname} onNavigate={closeMobile} />
              ) : (
                <Link key={l.label} to={l.to} onClick={closeMobile}
                  className={`py-2 border-b border-gold-500/10 text-sm font-semibold ${isActive(l) ? (branchMode ? accent.text : 'text-gold-400') : 'text-gray-200 hover:text-gold-400'}`}
                  data-testid={`mobile-${testId(`${branchMode ? branchMatch.key + '-' : ''}${l.label}`)}`}>
                  {l.label}
                </Link>
              )
            ))}
            {branchMode && (
              <Link to="/" onClick={closeMobile}
                className="py-2 text-xs text-gray-500 hover:text-gold-400 uppercase tracking-wider flex items-center gap-1 mt-1"
                data-testid="mobile-header-back-to-main">
                <ArrowLeft className="w-3 h-3" /> Back to main site
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
